import { type QueryClient } from '@tanstack/react-query';

import { Fonts } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { fetchFontMetadata, getPrivateFontMetadataKey } from '../hooks/queries/usePrivateFontMetadata';
import { getPrivateFontsListKey } from '../hooks/queries/usePrivateFontsList';
import { loadFontFace, parseFontVariant, registerFontKey } from '../nodes/Text/fonts';

type TextFontInfo = {
  fontFamily: string;
  fontFileUrl?: string;
  fontMetadataUrl?: string;
  fontSource?: 'builtIn' | 'file';
  fontVariant?: string;
};

async function preloadSingleFont(queryClient: QueryClient, font: TextFontInfo): Promise<void> {
  const variant = font.fontVariant ?? 'regular';
  const { fontWeight, fontStyle } = parseFontVariant(variant);

  if (font.fontSource === 'file' && font.fontFileUrl) {
    registerFontKey(font.fontFamily, font.fontFamily);
    await loadFontFace(font.fontFamily, font.fontFileUrl, {
      style: fontStyle,
      weight: String(fontWeight),
    });

    return;
  }

  // Built-in font: use fontMetadataUrl directly if available, otherwise look up in the font list
  let metadataUrl = font.fontMetadataUrl;

  if (!metadataUrl) {
    const fontsListData = await queryClient.ensureQueryData({
      queryFn: () => callAirApi(() => Fonts.list({ all: true })),
      queryKey: getPrivateFontsListKey({ all: true }),
      staleTime: Infinity,
    });

    metadataUrl = fontsListData.data.find((f) => f.key === font.fontFamily)?.metadataUrl;
  }

  if (!metadataUrl) {
    return;
  }

  const metadata = await queryClient.ensureQueryData({
    queryFn: () => fetchFontMetadata(metadataUrl),
    queryKey: getPrivateFontMetadataKey({ metadataUrl }),
    staleTime: Infinity,
  });

  const fileUrl = metadata.files[variant];

  if (!fileUrl) {
    return;
  }

  registerFontKey(font.fontFamily, metadata.family);
  await loadFontFace(metadata.family, fileUrl, {
    style: fontStyle,
    weight: String(fontWeight),
  });
}

/**
 * Preloads fonts for text edits so they're cached before the Text component
 * mounts. This runs during agent edit processing (before nodes are added to
 * the canvas), preventing a visible FOUC that would otherwise occur while
 * `useLoadFont` fetches the font after render. Uses React Query cache to
 * avoid duplicate network requests.
 */
export async function preloadFontsForTextEdits(queryClient: QueryClient, textEdits: TextFontInfo[]): Promise<void> {
  const uniqueFonts = new Map<string, TextFontInfo>();
  for (const edit of textEdits) {
    const key = `${edit.fontFamily}:${edit.fontVariant ?? 'regular'}`;

    if (!uniqueFonts.has(key)) {
      uniqueFonts.set(key, edit);
    }
  }

  const results = await Promise.allSettled(
    [...uniqueFonts.values()].map((font) => preloadSingleFont(queryClient, font)),
  );

  for (const result of results) {
    if (result.status === 'rejected') {
      reportErrorToBugsnag({
        context: 'Failed to preload font for grouped text edit',
        error: result.reason,
      });
    }
  }
}
